import moment from "moment";
import {
  Alignment,
  Content,
  ContentStack,
  ContentTable,
  ContentText,
  ContextPageSize,
  Position,
  TDocumentDefinitions,
} from "pdfmake/interfaces";

import getPdfMake from "react-lib/appcon/common/pdfMake";

import CompanyLogoForm, { CompanyLogoLayoutConfig, CompanyType, FontType } from "./CompanyLogoForm";

import { calculateAgeAndDOB, getDataURLFromURL, splitStringNewLine } from "./CommonInterface";

import {
  beToAd,
  dateToStringBE,
  dateToStringWithoutTimeBE,
  formatADDateToThaiTextDate,
  formatISOtoBEDateTime,
} from "react-lib/utils/dateUtils";

import CONFIG from "config/config";
import { Cover } from "../HRC/pdfForm/CheckupReportType";

// Common type for shared properties
export type CommonProps = PageMarginsProps & {
  font?: FontType;
  form?: string;
  headerMargins?: TDocumentDefinitions["pageMargins"];
  images?: Record<string, any>;
  logoHeight?: number;
  maxWidth?: number;
  styles?: TDocumentDefinitions["styles"];
  type?: number;
};

type PageMarginsProps = {
  pageMarginBottom?: number;
  pageMarginLeft?: number;
  pageMarginRight?: number;
  pageMargins?: TDocumentDefinitions["pageMargins"];
  pageMarginTop?: number;
};

type BaseHeaderProps = CommonProps & {
  isFullAddress?: boolean;
  titleContent?: ContentStack["stack"];
};

type HeaderWithTitleContentProps = BaseHeaderProps & {
  hideLogo?: boolean;
  logoPosition?: Position;
  titleMargins?: TDocumentDefinitions["pageMargins"];
  titleName?: string;
};

type HeaderLogoWithPublishProps = BaseHeaderProps & {
  haveBorder?: boolean;
  hospitalFontSize?: number;
  isHospitalVertical?: boolean;
  publishContent: ContentStack["stack"];
};

// Common fields between both types
export type BaseHeaderDetail = {
  divisionName?: string;
  doctorName?: string;
  hn: string;
  number: string;
  patient_age: string; // 20 ปี 5 เดือน 14 วัน
  patient_birthdate: string;
  patient_gender_name: string;
  patientName: string;
  telMobile?: string;
};

// Original types with extended base interfaces
export type HeaderLogoWithPatientProps = BaseHeaderProps & {
  detail: BaseHeaderDetail & {
    address?: string;
    citizen_no?: string;
    citizen_passport?: string;
    profileImage?: string | null;
    started: string;
  };
  formName?: string;
};

export type HeaderPrintListProps = BaseHeaderProps & {
  detail: BaseHeaderDetail & {
    address?: string;
    adrText?: string;
    citizen_no?: string;
    citizen_passport?: string;
    doctor_number?: string;
    patient_citizen_no?: string;
    patient_gender_name?: string;
    patientNameEN?: string;
    patientNameTH?: string;
    started: string;
    visitDate?: string;
  };
  formName: string;
  nameFontSize?: number;
  displayFields?: DisplayFieldsProps;
};

export type HeaderPrintListLandscapeProps = BaseHeaderProps & {
  detail: BaseHeaderDetail & {
    allergies: string;
    ipd_room: string;
    visit_date: string;
  };
  formName: string;
};

export type HeaderNonLogoWithPatientProps = BaseHeaderProps & {
  detail: BaseHeaderDetail & {
    allergies?: string;
    coverage?: string;
    patient_age_split?: string[];
    profileImage?: string | null;
    started?: string;
  };
  formName: string;
};

export type HeaderCheckupReportProps = BaseHeaderProps & {
  detail: Cover;
  language: "th" | "en";
  isMainReport: Boolean;
};

type HeaderOperativeScheduleProps = CommonProps & {
  hospitalContent: ContentStack["stack"];
  printDateContent: ContentStack["stack"];
  titleContent: ContentText["text"];
};

type GetAddressCompanyProps = Partial<{
  company: CompanyType;
  isFull: boolean;
}>;

type DisplayFieldsProps = [
  [boolean, boolean],
  [boolean, boolean],
  [boolean, boolean],
  [boolean, boolean],
  [boolean, boolean]
];

const FORM_NAME = "HeaderPdfFormTemplate";

const getNameCompanyTH = (company: CompanyType = CONFIG.COMPANY) =>
  company === "CU" ? "โรงพยาบาลคณะทันตแพทยศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย" : "";

const getNameCompanyEN = (company: CompanyType = CONFIG.COMPANY) =>
  company === "CU" ? "Chulalongkorn University Faculty of Dentistry Hospital" : "";

const CU_ADDRESS = {
  stack: [
    { alignment: "right", text: "โรงพยาบาลคณะทันตแพทยศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย" },
    { alignment: "right", text: "34  ถนนอังรีดูนังต์  แขวงวังใหม่  เขตปทุมวัน" },
    { alignment: "right", text: "กรุงเทพมหานคร  10330  โทร 0 2218 8635" },
  ],
};

const SAPIENS_ADDRESS = {
  table: {
    body: [
      [
        {
          alignment: "right",
          border: [false, false, true, false],
          stack: [
            {
              bold: true,
              font: "KanitLM",
              fontSize: 7,
              lineHeight: 1.05,
              text: "โรงพยาบาลเซเปี้ยนซ์",
            },
            {
              font: "KanitLM",
              fontSize: 7,
              lineHeight: 1.05,
              noWrap: true,
              text: "498/7 ซอยรามคำแหง 39 (เทพลีลา 1)",
            },
            {
              font: "KanitLM",
              fontSize: 7,
              lineHeight: 1.05,
              noWrap: true,
              text: "แขวงวังทองหลาง เขตวังทองหลาง",
            },
            {
              font: "KanitLM",
              fontSize: 7,
              lineHeight: 1.05,
              text: "กรุงเทพมหานคร 10310",
            },
          ],
        },
        {
          alignment: "right",
          border: [true, false, false, false],
          stack: [
            {
              bold: true,
              font: "Poppins",
              fontSize: 7,
              lineHeight: 1.175,
              text: "Sapiens Pain Hospital",
            },
            {
              font: "Poppins",
              fontSize: 7,
              lineHeight: 1.175,
              noWrap: true,
              text: "498/7 Soi Ramkhamhaeng 39 (Thepleela 1)",
            },
            {
              font: "Poppins",
              fontSize: 7,
              lineHeight: 1.175,
              noWrap: true,
              text: "Wang Thonglang, Wang thonglang",
            },
            {
              font: "Poppins",
              fontSize: 7,
              lineHeight: 1.175,
              text: "Bangkok 10310",
            },
          ],
        },
      ],
      [
        {
          alignment: "right",
          border: [false, false, false, false],
          colSpan: 2,
          font: "Poppins",
          fontSize: 7,
          text: "Tel. 02-111-3703",
        },
        {},
      ],
    ],
  },
};

const RAMASRI_ADDRESS = {
  stack: [
    {
      bold: true,
      font: "KanitLM",
      fontSize: 10,
      noWrap: true,
      text: "ศูนย์การแพทย์รามาธิบดีศรีอยุธยา มูลนิธิรามาธิบดีฯ",
    },
    {
      bold: true,
      font: "KanitLM",
      fontSize: 7,
      noWrap: true,
      text: "Ramathibodi Sri Ayutthaya Medical Center, Ramathibodi Foundation",
    },
    {
      font: "KanitLM",
      fontSize: 7,
      noWrap: true,
      text: "เลขที่ 404 ถนนศรีอยุธยา แขวงถนนพญาไท เขตราชเทวี กรุงเทพมหานคร 10400",
    },
    {
      font: "KanitLM",
      fontSize: 7,
      noWrap: true,
      text: "404 Si Ayutthaya Road, Khwaeng Phaya Thai, Khet Rachathewi, Bangkok 10400",
    },
  ],
};

const ADDRESS_MAP = {
  CU: CU_ADDRESS,
  RAMASRI: RAMASRI_ADDRESS,
  SAPIENS: SAPIENS_ADDRESS,
} as Record<CompanyType, any>;

export const getAddressCompany = ({
  company = CONFIG.COMPANY,
  isFull = false,
}: GetAddressCompanyProps = {}) => {
  const defaultAddress = {
    alignment: "right",
    text: `${company} Have no Address`,
  };

  // Long Address Case
  if (isFull) {
    switch (company) {
      case "MSK": {
        return {
          stack: [
            {
              alignment: "right",
              bold: true,
              font: "KanitLM",
              fontSize: 10,
              text: "โรงพยาบาลเอ็มเอสเค | MSK Hospital",
            },
            {
              alignment: "right",
              font: "KanitLM",
              fontSize: 7,
              text: "เลขที่ 118 ถ.สาทรเหนือ แขวงสีลม เขตบางรัก กรุงเทพมหานคร 10500",
            },
            {
              alignment: "right",
              font: "KanitLM",
              fontSize: 7,
              text: "118, Sathon Nuea Rd., Silom, Bang Rak, Bangkok 10500",
            },
            {
              alignment: "right",
              font: "KanitLM",
              fontSize: 7,
              text: "T +66 2250 9535 | F +66 2250 9565",
            },
          ],
        };
      }

      default: {
        return ADDRESS_MAP[company] ?? defaultAddress;
      }
    }
  }

  // default Address (Short Address)
  switch (company) {
    case "MSK": {
      return {
        table: {
          body: [
            [
              {
                alignment: "right",
                border: [false, false, true, false],
                margin: [0, 0, 5, 0],
                stack: [
                  {
                    bold: true,
                    font: "KanitLM",
                    fontSize: 7,
                    text: "โรงพยาบาลเอ็มเอสเค",
                  },
                  {
                    font: "KanitLM",
                    fontSize: 7,
                    text: "เลขที่ 118 ถ. สาทรเหนือ แขวงสีลม",
                  },
                  {
                    font: "KanitLM",
                    fontSize: 7,
                    text: "เขตบางรัก กรุงเทพมหานคร 10500",
                  },
                ],
              },
              {
                alignment: "right",
                border: [true, false, false, false],
                margin: [5, 0, 0, 0],
                stack: [
                  {
                    bold: true,
                    font: "KanitLM",
                    fontSize: 7,
                    text: "MSK Hospital",
                  },
                  {
                    font: "KanitLM",
                    fontSize: 7,
                    text: "118, Sathon Nuea Rd., Silom,",
                  },
                  {
                    font: "KanitLM",
                    fontSize: 7,
                    text: "Bang Rak, Bangkok 10500",
                  },
                ],
              },
            ],
          ],
        },
        width: "auto",
        layout: {
          hLineWidth: () => 0,
          vLineColor: () => "black",
          vLineWidth: (index: number) => (index === 1 ? 0.75 : 0),
        },
      };
    }

    default: {
      return ADDRESS_MAP[company] ?? defaultAddress;
    }
  }
};

// Common setup functions
const setupLogoAndFont = async (props: CommonProps & { alignment?: Alignment }) =>
  CompanyLogoForm({
    alignment: props.alignment,
    font: props.font || "KanitLM",
    form: props.form || FORM_NAME,
    height: props.logoHeight ?? 50,
    maxWidth: props.maxWidth,
    type: props.type,
  });

const getPageMargins = (
  props: PageMarginsProps,
  options: { overrideMargins?: boolean } = {}
): TDocumentDefinitions["pageMargins"] => {
  const defaultMargin = 15;
  const defaultTop = 100;

  // ฟังก์ชันย่อยสำหรับกำหนด margins array
  const buildMarginsArray = (
    left: number = defaultMargin,
    top: number = defaultTop,
    right: number = defaultMargin,
    bottom: number = defaultMargin
  ): [number, number, number, number] => [left, top, right, bottom];

  if (props.pageMargins !== undefined) {
    if (Array.isArray(props.pageMargins) && options.overrideMargins) {
      const [originalLeft, originalTop, originalRight, originalBottom] = props.pageMargins;

      return buildMarginsArray(
        props.pageMarginLeft ?? originalLeft,
        props.pageMarginTop ?? originalTop,
        props.pageMarginRight ?? originalRight,
        props.pageMarginBottom ?? originalBottom
      );
    }

    return props.pageMargins;
  }

  return buildMarginsArray(
    props.pageMarginLeft,
    props.pageMarginTop,
    props.pageMarginRight,
    props.pageMarginBottom
  );
};

const shouldShowLogo = () => !CONFIG.HIDE_COMPANY_LOGO_PRINT;

const getCompanyName = (isVertical: boolean, fontSize: number) => ({
  stack: [
    { bold: true, fontSize, text: getNameCompanyTH() },
    { fontSize, text: getNameCompanyEN() },
  ],
});

type CalculateHeaderBasedMarginsProps = Pick<
  CompanyLogoLayoutConfig,
  "font" | "fonts" | "fontSizes" | "lineHeights"
> &
  Pick<TDocumentDefinitions, "header" | "images" | "pageMargins" | "styles">;

export const calculateHeaderBasedMargins = async (headerForm: CalculateHeaderBasedMarginsProps) => {
  const headerDef: TDocumentDefinitions = {
    images: headerForm.images,
    pageMargins: [0, 0, 0, 0],
    styles: headerForm.styles,
    content:
      typeof headerForm.header === "function"
        ? headerForm.header(0, 0, {} as ContextPageSize) || []
        : headerForm.header || [],
  };

  const pdfMake = await getPdfMake(true);

  const pdfHeader = pdfMake.createPdf(headerDef);

  return {
    ...headerForm,
    pageMargins: getPageMargins(
      { ...headerForm, pageMarginTop: pdfHeader.getStream().y + 24.5214 },
      { overrideMargins: true }
    ),
  };
};

/**========================================================================
 **                           TEMPLATE HEADER
 *========================================================================**/

// Template #1 Logo Left and Address Right (Can Add Title Content in Center) / มีโลโก้อยู่ด้านซ้าย - ข้อมูลที่อยู่อยู่ด้านขวา
export const HeaderLogoWithAddress = async (props: BaseHeaderProps) => {
  const { columns, font, fonts, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
    maxWidth: props.maxWidth ?? 250,
  });
  const pageMargins = getPageMargins(props);

  return {
    font,
    fonts,
    fontSizes,
    images: { ...images, ...props.images },
    lineHeights,
    pageMargins,
    styles: { ...props.styles },
    header: () => ({
      margin: props.headerMargins ?? [15, 15, 15, 0],
      table: {
        widths: shouldShowLogo() ? ["25%", "*", "auto"] : ["25%", "*", "45%"],
        body: [
          ...(columns.length > 0
            ? [[{ columns }, "", getAddressCompany({ isFull: props.isFullAddress })]]
            : []),
          props.titleContent
            ? [{ colSpan: 3, stack: props.titleContent }]
            : [{ colSpan: 3, text: "" }],
        ],
      },
      layout: "noBorders", // Comment this For Checking Widths and Layout of Header
    }),
  };
};

// Template #2 Logo and Title Content Report
export const HeaderLogoWithTitleContent = async (props: HeaderWithTitleContentProps) => {
  const { columns, font, fontSizes, images, lineHeights } = await setupLogoAndFont(props);
  const pageMargins = getPageMargins(props);
  const logoCompanyPosition = CONFIG.COMPANY === "MSK" ? { x: 20, y: 20 } : { x: 20, y: 30 };
  const defaultLogoPosition = props.logoPosition ?? logoCompanyPosition;

  return {
    font,
    fontSizes,
    images: { ...images, ...props.images },
    lineHeights,
    pageMargins,
    styles: { ...props.styles },
    header: () => ({
      margin: props.headerMargins ?? [15, 15, 15, 0],
      // Table Solution
      // table: {
      //   widths: ["10%", "90%"],
      //   body: !CONFIG.HIDE_COMPANY_LOGO_PRINT
      //   ? [
      //       [
      //         {image: "logo", width: companyLogoForm.width, height: defaultLogoHeight, absolutePosition: {x: 20, y: 24}},
      //         {
      //           stack: CONFIG.COMPANY === "CU"
      //             ? [
      //                 {text: `CHULALONGKORN UNIVERSITY, FACULTY OF DENTISTRY`, bold: true, fontSize: 18},
      //                 ...props.titleContent
      //             ]
      //             : props.titleContent
      //         }
      //       ], // issue 65030
      //   ]
      //   : [
      //     [{stack: props.titleContent, colSpan: 2}],
      //   ],
      // },
      // layout: 'noBorders', // Comment this For Checking Widths and Layout of Header

      // Stack Solution
      stack: [
        props.hideLogo
          ? { text: "" }
          : {
              absolutePosition: defaultLogoPosition,
              columns,
            },
        {
          stack:
            CONFIG.COMPANY === "CU"
              ? [
                  {
                    alignment: "center",
                    bold: true,
                    fontSize: 18,
                    margin: props.titleMargins ?? [0, 0, 0, 0],
                    text: props.titleName || "CHULALONGKORN UNIVERSITY, FACULTY OF DENTISTRY",
                  },
                  ...(props.titleContent ?? []),
                ]
              : props.titleContent,
        },
      ],
    }),
  };
};

// Tempalte #3 Header Logo + Title + Publish Detail
export const HeaderLogoWithPublishDetail = async (props: HeaderLogoWithPublishProps) => {
  const { columns, font, fontSizes, images, lineHeights } = await setupLogoAndFont(props);
  const pageMargins = getPageMargins(props);
  const defaultHospitalFontSize = props.hospitalFontSize ?? 10;
  const isDefaultHaveBorder = props.haveBorder ?? false;

  return {
    font,
    fontSizes,
    images: { ...images, ...props.images },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
    },
    header: (currentPage: number, countPage: number) => ({
      margin: props.headerMargins ?? [15, 15, 15, 0],
      table: {
        widths: ["auto", "*", "20%"],
        body: [
          [
            props.isHospitalVertical
              ? {
                  ...(isDefaultHaveBorder ? { border: [true, true, false, true] } : {}),
                  // border: isDefaultHaveBorder ? [true, true, false, true] : [false, false, false, false],
                  margin: [2, 1, 0, 1],
                  stack:
                    columns.length > 0
                      ? [{ columns }, getCompanyName(true, defaultHospitalFontSize)]
                      : [],
                }
              : {
                  ...(isDefaultHaveBorder ? { border: [true, true, false, true] } : {}),
                  // border: isDefaultHaveBorder ? [true, true, false, true] : [false, false, false, false],
                  columns:
                    columns.length > 0
                      ? [{ columns }, getCompanyName(false, defaultHospitalFontSize)]
                      : [],
                  margin: [2, 1, 0, 1],
                },
            {
              ...(isDefaultHaveBorder ? { border: [false, true, false, true] } : {}),
              // border: isDefaultHaveBorder ? [false, true, false, true] : [false, false, false, false],
              stack: props.titleContent,
            },
            {
              ...(isDefaultHaveBorder ? { border: [false, true, true, true] } : {}),
              // border: isDefaultHaveBorder ? [false, true, true, true] : [false, false, false, false],
              margin: [0, 7.5, 2, 0],
              stack: [
                ...props.publishContent,
                {
                  alignment: "right",
                  fontSize: 12,
                  text: `หน้าที่ ${currentPage}/${countPage}`,
                },
              ],
            },
          ],
        ],
      },
    }),
  };
};

// Tempalte #4 Header Logo + Title + Patient Detail + Image
export const HeaderLogoWithPatientDetailImage = async (props: HeaderLogoWithPatientProps) => {
  const { columns, font, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
    alignment: "center",
    maxWidth: 230,
  });
  const pageMargins = getPageMargins(props);

  const profileImages = await getDataURLFromURL([{ image: props.detail.profileImage || "" }]);

  return {
    font,
    fontSizes,
    images: {
      ...images,
      ...props.images,
      userProfile: profileImages[0].image || `${origin}/static/images/user.png`,
    },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      fieldHeader: { alignment: "center", bold: true, fontSize: fontSizes[14.5] },
      fieldHeaderDivision: { alignment: "center", fontSize: fontSizes[13] },
      fieldHeaderKey: { bold: true, /* font: "PoppinsLM",*/ fontSize: fontSizes[13] },
      fieldHeaderValue: { fontSize: fontSizes[13.5] },
    } satisfies TDocumentDefinitions["styles"],
    header: () => ({
      stack: [
        {
          columns: [
            {
              stack: [
                {
                  columns,
                  margin: [-60, -5, 0, 0],
                },
                {
                  margin: [-60, 10, 0, 0],
                  style: "fieldHeader",
                  text: props.formName ?? "OPD SUMMARY REPORT",
                },
                {
                  margin: [-60, -2.5, 0, 0],
                  style: "fieldHeaderDivision",
                  text: props.detail.divisionName,
                },
              ],
              width: "51%",
            },
            {
              lineHeight: lineHeights[0.9],
              marginTop: -2.5,
              stack: [
                {
                  text: [
                    { style: "fieldHeaderKey", text: "HN [EN]: " },
                    {
                      style: "fieldHeaderValue",
                      text: `${props.detail.hn} [${props.detail.number}]`,
                    },
                  ],
                },
                {
                  text: [
                    { style: "fieldHeaderKey", text: "Name: " },
                    { style: "fieldHeaderValue", text: props.detail.patientName },
                  ],
                },
                {
                  columns: [
                    {
                      text: [
                        { style: "fieldHeaderKey", text: "Birthdate: " },
                        {
                          style: "fieldHeaderValue",
                          text: beToAd(props.detail.patient_birthdate)?.format("D MMMM YYYY"),
                        },
                      ],
                      width: "auto",
                    },
                    {
                      marginLeft: 20,
                      text: [
                        { style: "fieldHeaderKey", text: "Sex: " },
                        { style: "fieldHeaderValue", text: props.detail.patient_gender_name },
                      ],
                      width: "*",
                    },
                  ],
                },
                {
                  text: [
                    { style: "fieldHeaderKey", text: "Age: " },
                    {
                      style: "fieldHeaderValue",
                      text: calculateAgeAndDOB(props.detail.patient_age).fullage_en,
                    },
                  ],
                },
                {
                  text: [
                    { style: "fieldHeaderKey", text: "Visit date: " },
                    {
                      style: "fieldHeaderValue",
                      text: moment(props.detail.started).format("D/M/YYYY"),
                    },
                  ],
                },
                {
                  text: [
                    { style: "fieldHeaderKey", text: "Physician: " },
                    { style: "fieldHeaderValue", text: props.detail.doctorName },
                  ],
                },
              ],
              style: "miniField",
              width: "*",
            },
            { text: "", width: 12.5 },
            {
              alignment: "center",
              height: 85,
              image: "userProfile",
              width: 70,
            },
            { text: "", width: 0 },
          ],
          margin: [10, 10, 10, 0],
        },
        {
          marginTop: 5,
          table: {
            widths: ["100%"],
            body: [[{ border: [false, true, false, false], text: "" }]],
          },
        },
      ] as ContentStack["stack"],
    }),
  };
};

// Template #5 Header Patient Detail
export const HeaderPatientDetail = async (
  props: HeaderPrintListProps & {
    content: ContentStack["stack"];
    displayFields?: (boolean | string)[][];
    layout?: "normal" | "wide";
    nameFontSize?: number;
  }
) => {
  const alignment = props.formName ? "center" : "left";
  const pageMargins = getPageMargins(props);

  const { columns, font, fonts, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
    alignment,
    maxWidth: 230,
  });

  const isWide = props.layout === "wide";
  const gender = (props.detail.patient_gender_name || "").toLowerCase();
  const key = ["female", "male"].includes(gender) ? gender : "unspecified";
  const icons = {
    female: { width: 6.5 },
    male: { width: 10 },
    unspecified: { width: 10 },
  }[key];

  const detailHN = `${props.detail.hn}`;
  const detailNumber = props.detail.number ? `[${props.detail.number}]` : "";

  const patientFields = [
    [
      {
        label: "HN [EN]: ",
        value: [
          { text: `${detailHN} ${detailNumber}`, width: "auto" },
          ...(props.formName
            ? []
            : [
                {
                  ...icons,
                  height: 10,
                  image: key,
                  margin: [7.5, 2, 0, 0],
                },
              ]),
        ],
      },
      { label: "VisitDate: ", value: props.detail.visitDate, width: 47.5 },
    ],
    [
      { label: "Name: ", value: props.detail.patientName },
      { label: "Sex: ", value: props.detail.patient_gender_name },
    ],
    [
      {
        label: "Birthdate: ",
        value: beToAd(props.detail.patient_birthdate)?.format("D MMMM YYYY"),
      },
      { label: "Age: ", value: calculateAgeAndDOB(props.detail.patient_age).fullage_en },
    ],
    [
      {
        label: "CitizenID/ Passport: ",
        value:
          props.detail.patient_citizen_no ||
          props.detail.citizen_passport ||
          props.detail.citizen_no,
      },
      { label: "Tel: ", value: props.detail.telMobile },
    ],
    [
      {
        label: "ADR: ",
        value: splitStringNewLine(props.detail.adrText || "", {
          ellipse: "...",
          fontSize: fontSizes[13],
          max: 1,
          width: props.formName ? 500 : 285,
        }).map((text) => ({ noWrap: true, style: "fieldHeaderValue", text })),
      },
    ],
  ];

  const displayFields = props.displayFields ?? [
    [true, false], // Row 1: Show HN, hide VisitDate
    [true, true], // Row 2: Show Name and Sex
    [true, true], // Row 3: Show Birthdate and Age
    [true, true], // Row 4: Show CitizenID and Tel
    [false, false], // Row 5: ADR field
  ];

  // Transform data into formatted columns structure
  const formattedColumns = patientFields
    .map((row, rowIndex) =>
      displayFields[rowIndex].filter(Boolean).map((unused, index) => patientFields[rowIndex][index])
    )
    .filter((row) => row.length > 0)
    .map((row, rowIndex) => ({
      columns: row.map((item, index) => {
        const display = displayFields[rowIndex][index];
        const label = typeof display === "string" ? display : item.label;

        return index === 0
          ? {
              ...(row.length === 2 && { width: isWide ? 200 : 215 }),
              ...(Array.isArray(item.value)
                ? {
                    columns: [
                      { marginRight: 5, style: "fieldHeaderKey", text: label, width: "auto" },
                      { columns: item.value, marginTop: -0.15, style: "fieldHeaderValue" },
                    ],
                    noWrap: true,
                  }
                : {
                    noWrap: true,
                    text: [
                      {
                        style: "fieldHeaderKey",
                        text: label,
                      },
                      {
                        style: "fieldHeaderValue",
                        text: item.value,
                      },
                    ],
                  }),
            }
          : {
              alignment: "left",
              noWrap: true,
              stack: [
                {
                  alignment: "left",
                  columns: [
                    {
                      style: "fieldHeaderKey",
                      text: item.label,
                      ...("width" in item ? { width: item.width } : { width: 25 }),
                    },
                    {
                      marginTop: -0.15,
                      style: "fieldHeaderValue",
                      text: item.value,
                      width: 70,
                    },
                  ],
                },
              ],
              width: "*",
            };
      }),
    }));

  return {
    font,
    fonts,
    fontSizes,
    images: {
      ...images,
      ...props.images,
      female: `${origin}/static/images/rehab/female-gender-blue.png`,
      male: `${origin}/static/images/rehab/male-gender-blue.png`,
      unspecified: `${origin}/static/images/rehab/unspecified-gender-blue.png`,
    },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      fieldHeader: {
        alignment: "center",
        bold: true,
        font: fonts.KANIT_LM,
        fontSize: fontSizes[14.5],
      },
      fieldHeaderDivision: {
        alignment: isWide ? undefined : "center",
        bold: isWide,
        font: fonts.KANIT_LM,
        fontSize: fontSizes[isWide ? 18 : 13],
      },
      fieldHeaderKey: { bold: true, font: fonts.POPPINS_LM, fontSize: fontSizes[13] },
      fieldHeaderReport: {
        alignment: "center",
        bold: true,
        font: fonts.POPPINS_LM,
        fontSize: fontSizes[18],
      },
      fieldHeaderValue: { font: fonts.KANIT_LM, fontSize: fontSizes[13.5] },
    } satisfies TDocumentDefinitions["styles"],
    header: () => ({
      stack: [
        {
          columns: [
            {
              stack: [
                {
                  columns,
                  margin: [alignment === "center" ? -15 : -5, -5, 0, 0],
                },
                ...(isWide
                  ? [
                      {
                        margin: [alignment === "center" ? 20 : 2.5, 0, 0, 0],
                        style: "fieldHeaderDivision",
                        text: props.detail.divisionName,
                      },
                    ]
                  : [
                      {
                        margin: [-15, 10, 0, 0],
                        style: "fieldHeader",
                        text: props.formName,
                        ...(props.nameFontSize && { fontSize: props.nameFontSize }),
                      },
                      {
                        margin: [-15, -2.5, 0, 0],
                        style: "fieldHeaderDivision",
                        text: props.detail.divisionName,
                      },
                    ]),
              ],
              width: isWide && props.formName ? "25%" : "45%",
            },
            // Center column (wide layout only)
            ...(isWide && props.formName
              ? [
                  {
                    margin: [0, 20, 0, 0],
                    style: "fieldHeaderReport",
                    text: props.formName || "",
                    width: "*",
                  },
                ]
              : []),
            {
              lineHeight: lineHeights[0.9],
              marginTop: -2.5,
              stack: [...formattedColumns, ...props.content],
              style: "miniField",
              width: isWide ? "45%" : "*",
            },
          ],
          margin: [10, 10, 10, 0],
        },
        {
          marginTop: 5,
          table: {
            widths: ["100%"],
            body: [[{ border: [false, true, false, false], text: "" }]],
          },
          layout: {
            hLineColor: () => "#606060",
            hLineWidth: () => 1,
            vLineWidth: () => 0,
          },
        },
      ] as ContentStack["stack"],
    }),
  };
};

// Tempalte #6 Header Logo + Title + Patient Detail
export const HeaderLogoWithPatientDetail = async (props: HeaderPrintListProps) => {
  const headerForm = await HeaderPatientDetail({
    ...props,
    content: [
      {
        columns: [
          {
            marginRight: 2.5,
            style: "fieldHeaderKey",
            text: "Address:",
            width: "auto",
          },
          {
            style: "fieldHeaderValue",
            text: props.detail.address || "",
            width: "*",
          },
        ],
      },
    ],
  });

  return calculateHeaderBasedMargins(headerForm);
};

// Template #7 Header Print List (based on Sapiens)
export const HeaderPrintList = async (props: HeaderPrintListProps) => {
  const doctorNumber = props.detail.doctor_number ? ` [${props.detail.doctor_number}]` : "";

  return HeaderPatientDetail({
    ...props,
    nameFontSize: props.nameFontSize,
    content: [
      {
        style: "fieldHeaderValue",
        text: [
          { bold: true, text: "Visit date: " },
          { text: moment(props.detail.started).format("D/M/YYYY") },
        ],
      },
      {
        style: "fieldHeaderValue",
        text: [
          { bold: true, text: "Physician: " },
          { text: `${props.detail.doctorName}${doctorNumber}` },
        ],
      },
    ],
  });
};

// Template #8 Header Print List (Landscape)
export const HeaderPrintListLandscape = async (props: HeaderPrintListLandscapeProps) => {
  const { columns, font, fonts, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
    alignment: "left",
    maxWidth: 230,
  });

  const pageMargins = getPageMargins(props);

  const isSarabun = props.font === "THSarabunNew";

  return {
    font,
    fonts,
    fontSizes,
    images: {
      ...images,
      ...props.images,
    },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      fieldHeader: {
        alignment: "center",
        bold: true,
        font: fonts.KANIT_LM,
        fontSize: fontSizes[18],
      },
      fieldHeaderDivision: { alignment: "center", font: fonts.KANIT_LM, fontSize: fontSizes[13] },
      fieldHeaderKey: {
        bold: true,
        font: fonts.POPPINS_LM,
        fontSize: isSarabun ? fontSizes[14] : fontSizes[12],
      },
      fieldHeaderValue: {
        font: fonts.KANIT_LM,
        fontSize: isSarabun ? fontSizes[14] : fontSizes[12],
      },
    } satisfies TDocumentDefinitions["styles"],
    header: () => [
      {
        margin: props.headerMargins ?? [0, 0, 0, 0],
        table: {
          widths: ["60%", "40%"],
          body: [
            [
              {
                stack: [
                  {
                    columns,
                    margin: [5, 0, 0, 8],
                  },
                  {
                    margin: [16, 10, 0, 0],
                    table: {
                      body: [
                        [
                          {
                            margin: [16, 0, 16, 0],
                            style: "fieldHeader",
                            text: props.formName,
                          },
                        ],
                      ],
                    },
                  },
                ],
              },
              {
                stack: [
                  {
                    columns: [
                      {
                        style: "fieldHeaderKey",
                        text: "Name:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 176,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 176,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                      {
                        style: "fieldHeaderKey",
                        text: "HN:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 100,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 100,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                    ],
                    width: "auto",
                  },
                  {
                    columns: [
                      {
                        style: "fieldHeaderKey",
                        text: "Date of Birth:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 100,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 100,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                      {
                        style: "fieldHeaderKey",
                        text: "Age:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 50,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 50,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                      {
                        style: "fieldHeaderKey",
                        text: "Gender:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 56,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 56,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                    ],
                    width: "auto",
                  },
                  {
                    columns: [
                      {
                        style: "fieldHeaderKey",
                        text: "VN/ AN:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 120,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 120,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                      {
                        style: "fieldHeaderKey",
                        text: "Visit Date:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 120,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 120,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                    ],
                    width: "auto",
                  },
                  {
                    columns: [
                      {
                        style: "fieldHeaderKey",
                        text: "Attending Physician:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 150,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 150,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                      {
                        style: "fieldHeaderKey",
                        text: "Room:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 50,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 50,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                    ],
                    width: "auto",
                  },
                  {
                    columns: [
                      {
                        style: "fieldHeaderKey",
                        text: "Allergies:",
                        width: "auto",
                      },
                      {
                        canvas: [
                          isSarabun
                            ? {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 279,
                                y1: 15,
                                y2: 15,
                              }
                            : {
                                lineWidth: 1,
                                type: "line",
                                x1: 0,
                                x2: 279,
                                y1: 13,
                                y2: 13,
                              },
                        ],
                        width: "auto",
                      },
                    ],
                    width: "auto",
                  },
                ],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      // Position
      {
        absolutePosition: { x: 540, y: 2 },
        style: "fieldHeaderValue",
        text: props.detail.patientName,
      },
      {
        absolutePosition: { x: 740, y: 2 },
        style: "fieldHeaderValue",
        text: props.detail.hn,
      },
      {
        absolutePosition: { x: 600, y: isSarabun ? 20 : 17 },
        style: "fieldHeaderValue",
        text: props.detail.patient_birthdate,
      },
      {
        absolutePosition: { x: 705, y: isSarabun ? 20 : 17 },
        style: "fieldHeaderValue",
        text: props.detail.patient_age,
      },
      {
        absolutePosition: { x: 790, y: isSarabun ? 20 : 17 },
        style: "fieldHeaderValue",
        text: props.detail.patient_gender_name,
      },
      {
        absolutePosition: { x: 590, y: isSarabun ? 38 : 32 },
        style: "fieldHeaderValue",
        text: props.detail.number || "-",
      },
      {
        absolutePosition: { x: 740, y: isSarabun ? 38 : 32 },
        style: "fieldHeaderValue",
        text: props.detail.visit_date,
      },
      {
        absolutePosition: { x: 600, y: isSarabun ? 56 : 45 },
        style: "fieldHeaderValue",
        text: props.detail.doctorName,
      },
      {
        absolutePosition: { x: 780, y: isSarabun ? 56 : 45 },
        noWrap: true,
        style: "fieldHeaderValue",
        text: props.detail.ipd_room || "-",
      },
      {
        absolutePosition: { x: 550, y: isSarabun ? 74 : 60 },
        noWrap: true,
        style: "fieldHeaderValue",
        text:
          truncateTextSimple(
            props.detail.allergies,
            isSarabun ? 580 : 400,
            isSarabun ? fontSizes[14] : fontSizes[12]
          ) || "-",
      },
    ],
  };
};

// Template #9 Header Non-Logo + Patient/Encounter Detail + Image Profile
export const HeaderNonLogoWithPatientDetailImage = async (props: HeaderNonLogoWithPatientProps) => {
  const { font, fonts, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
  });

  const pageMargins = getPageMargins(props);
  const profileImage = await getDataURLFromURL([{ image: props.detail.profileImage || "" }]);

  return {
    font,
    fonts,
    fontSizes,
    images: {
      ...images,
      ...props.images,
      userProfile: profileImage[0].image || `${origin}/static/images/user.png`,
    },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      companyTitle: { bold: true, fontSize: fontSizes[16] },
      headerKey: { bold: true, preserveLeadingSpaces: true },
      nameTitle: { bold: true, fontSize: fontSizes[15] },
    },
    header: () => [
      {
        margin: props.headerMargins ?? [15, 15, 15, 0],
        table: {
          widths: ["40%", "40%", "20%"],
          body: [
            [
              {
                table: {
                  widths: ["*"],
                  body: [
                    [{ style: "companyTitle", text: getNameCompanyTH() }],
                    [{ style: "nameTitle", text: `\n${props.detail.divisionName || ""}` }],
                    [{ style: "nameTitle", text: props.formName || "" }],
                  ],
                },
                layout: "noBorders",
              },
              {
                table: {
                  widths: ["50%", "50%"],
                  body: [
                    [
                      {
                        columns: [
                          { style: "headerKey", text: "HN", width: "auto" },
                          { text: "", width: 5 },
                          { alignment: "center", text: props.detail.hn },
                        ],
                      },
                      {
                        columns: [
                          { style: "headerKey", text: "AN ", width: "auto" },
                          { text: "", width: 5 },
                          { alignment: "center", text: props.detail.number },
                        ],
                      },
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [{ text: "", width: 10 }, { text: props.detail.patientName }],
                      },
                      {},
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [
                          { style: "headerKey", text: "อายุ ", width: "auto" },
                          { text: "", width: 5 },
                          {
                            alignment: "center",
                            text: props.detail.patient_age_split?.[0],
                            width: 44,
                          },
                          { text: "", width: 5 },
                          { text: "ปี", width: "auto" },
                          { text: "", width: 5 },
                          {
                            alignment: "center",
                            text: props.detail.patient_age_split?.[1],
                            width: 44,
                          },
                          { text: "", width: 5 },
                          { text: "เดือน", width: "auto" },
                          { text: "", width: 5 },
                          {
                            alignment: "center",
                            text: props.detail.patient_age_split?.[2],
                            width: 44,
                          },
                          { text: "", width: 5 },
                          { text: "วัน", width: "auto" },
                        ],
                      },
                      {},
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [
                          { style: "headerKey", text: "วันเกิด ", width: "auto" },
                          { text: "", width: 5 },
                          {
                            columns: [
                              { text: "", width: 10 },
                              { text: props.detail.patient_birthdate },
                            ],
                          },
                        ],
                      },
                      {},
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [
                          { style: "headerKey", text: "วันที่เข้ารับบริการ ", width: "auto" },
                          { text: "", width: 5 },
                          {
                            columns: [
                              { text: "", width: 10 },
                              {
                                text: props.detail.started
                                  ? `${dateToStringBE(props.detail.started)} น.`
                                  : "",
                              },
                            ],
                          },
                        ],
                      },
                      {},
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [
                          { style: "headerKey", text: "สิทธิฯ ", width: "auto" },
                          { text: "", width: 5 },
                          {
                            text: props.detail.coverage
                              ? truncateTextSimple(props.detail.coverage, 340, fontSizes[12]) || "-"
                              : "-",
                          },
                        ],
                      },
                      {},
                    ],
                    [
                      {
                        colSpan: 2,
                        columns: [
                          { style: "headerKey", text: "Allergy ", width: "auto" },
                          { text: "", width: 5 },
                          {
                            text: props.detail.allergies
                              ? truncateTextSimple(props.detail.allergies, 300, fontSizes[12]) ||
                                "-"
                              : "-",
                          },
                        ],
                      },
                      {},
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 0,
                  paddingBottom: () => 0,
                  paddingLeft: () => 0,
                  paddingRight: () => 1,
                  paddingTop: () => 0,
                  vLineWidth: () => 0,
                },
              },
              {
                alignment: "center",
                height: 122,
                image: "userProfile",
                width: 98,
              },
            ],
          ],
        },
        layout: {
          vLineWidth: (i: number, node: any) => {
            return i === 0 || i === node.table.widths.length ? 1 : 0;
          },
        },
      },
      // Underline Position
      {
        absolutePosition: { x: 265, y: 33 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 88, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 374, y: 33 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 86, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 247, y: 51 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 213, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 267, y: 69 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 44, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 328, y: 69 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 44, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 402, y: 69 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 44, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 277, y: 88 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 183, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 319, y: 106 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 141, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 274, y: 123 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 186, y1: 0, y2: 0 }],
      },
      {
        absolutePosition: { x: 282, y: 141 },
        canvas: [{ lineWidth: 0.5, type: "line", x1: 0, x2: 178, y1: 0, y2: 0 }],
      },
    ],
  };
};

/**========================================================================
 **                           SPECIFIC HEADER
 *========================================================================**/

// Template #10 Header for FormOperativeSchedule
export const HeaderOperativeSchedule = async (props: HeaderOperativeScheduleProps) => {
  const { columns, font, fontSizes, images, lineHeights } = await setupLogoAndFont(props);
  const pageMargins = getPageMargins(props);

  return {
    font,
    fontSizes,
    images: { ...images, ...props.images },
    lineHeights,
    pageMargins,
    styles: { ...props.styles },
    header: () => ({
      margin: props.headerMargins ?? [15, 15, 15, 0],
      stack: [
        {
          columns: [
            { columns },
            { stack: props.hospitalContent, width: "auto" },
            { stack: props.printDateContent, width: "auto" },
          ],
        },
        {
          columns: [
            {
              canvas: [
                {
                  lineColor: "#e25f9e",
                  lineWidth: 2,
                  type: "line",
                  x1: 0,
                  x2: 890,
                  y1: 5,
                  y2: 5,
                },
              ],
            },
          ],
        },
        {
          columns: [props.titleContent],
        },
      ],
    }),
  };
};

// Template #11 Header For Form Summary Invoice IPD
export const HeaderSummaryInvoiceIpd = async (props: BaseHeaderProps) => {
  const { font, fonts, fontSizes, lineHeights } = await setupLogoAndFont({
    ...props,
    alignment: "left",
    maxWidth: 230,
  });
  const pageMargins = getPageMargins(props);
  const isSarabun = props.font === "THSarabunNew";
  const currentDate = dateToStringWithoutTimeBE(moment());
  const currentTime = moment().format("HH:mm");

  const getHospitalName = (company: CompanyType = CONFIG.COMPANY) => {
    switch (company) {
      case "CU":
        return "โรงพยาบาลคณะทันตแพทยศาสตร์ จุฬาลงกรณ์มหาวิทยาลัย";
      case "SAPIENS":
        return "โรงพยาบาลเซเปี้ยนซ์";
    }
  };

  // console.log("Yeti Header Props: ", props);

  // console.log("Yeti Header Summary Invoice IPD Date: ", currentDate);

  return {
    font,
    fonts,
    fontSizes,
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      fieldHeader: {
        alignment: "center",
        bold: true,
        font: fonts.KANIT_LM,
        fontSize: fontSizes[18],
      },
      fieldHeaderDivision: { alignment: "center", font: fonts.KANIT_LM, fontSize: fontSizes[13] },
      fieldHeaderKey: {
        bold: true,
        font: fonts.POPPINS_LM,
        fontSize: isSarabun ? fontSizes[14] : fontSizes[12],
      },
      fieldHeaderValue: {
        font: fonts.KANIT_LM,
        fontSize: isSarabun ? fontSizes[14] : fontSizes[12],
      },
    } satisfies TDocumentDefinitions["styles"],
    header: () => [
      {
        margin: props.headerMargins ?? [0, 0, 0, 0],
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              {
                stack: [
                  {
                    style: "fieldHeaderValue",
                    text: getHospitalName(),
                  },
                  {
                    style: "fieldHeaderValue",
                    text: `พิมพ์วันที่ ${currentDate} เวลา ${currentTime} น.`,
                  },
                ],
              },
              {
                stack: props.titleContent ?? [
                  {
                    text: "",
                  },
                ],
              },
            ],
          ],
        },
        // layout: "noBorders",
        layout: {
          hLineColor: function (i: any, node: any) {
            return i !== node.table.body.length ? "white" : "black";
          },
          vLineColor: function (i: any, node: any) {
            return "white";
          },
        },
      },
    ],
  };
};

// Template #12 Header Counts And Packing
export const HeaderPatientDetailWide = async (
  props: HeaderPrintListProps & {
    nameFontSize?: number;
  }
) =>
  HeaderPatientDetail({
    ...props,
    detail: {
      ...props.detail,
      patientName: `${props.detail.patientNameTH} ${
        props.detail.patientNameEN && `(${props.detail.patientNameEN})`
      }`,
    },
    displayFields: [
      ["HN [VN/AN]: ", true],
      [true, false],
      [true, true],
      [false, false],
      [true, false],
    ],
    layout: "wide",
    content: [],
  });

// Template #13 Header Checkup Reports
export const HeaderCheckupReport = async (props: HeaderCheckupReportProps) => {
  const pageMargins = getPageMargins(props);
  const { language = "th", isMainReport = true } = props;

  const { font, fonts, fontSizes, images, lineHeights } = await setupLogoAndFont({
    ...props,
    alignment: "left",
    maxWidth: 230,
  });

  const detail = props.detail;
  const isEng = language === "en";

  return {
    font,
    fonts,
    fontSizes,
    images: {
      ...images,
    },
    lineHeights,
    pageMargins,
    styles: {
      ...props.styles,
      header_info: {
        fontSize: fontSizes[12],
      },
    } satisfies TDocumentDefinitions["styles"],
    header: (currentPage: number, pageCount: number): ContentTable | null => {
      if (isMainReport && currentPage === 1) return null;

      return {
        table: {
          widths: ["50%", "50%"],
          body: [
            [
              {
                image: "logo",
                alignment: "center",
                width: 120,
              },
              {
                table: {
                  widths: ["45%", "55%"],
                  body: [
                    [
                      {
                        text: isEng ? "Medical Check Up Report" : "รายงานผล การตรวจสุขภาพประจำปี",
                        style: "header_info",
                        colSpan: 2,
                      },
                      {},
                    ],
                    [
                      {
                        text: [
                          { text: isEng ? "Name: " : "ชื่อ: " },
                          { text: detail.patient_name },
                        ],
                        style: "header_info",
                        colSpan: 2,
                      },
                      {},
                    ],
                    [
                      {
                        text: [{ text: "HN: " }, { text: detail.patient_hn }],
                        style: "header_info",
                      },
                      {
                        text: [{ text: isEng ? "Sex: " : "เพศ: " }, { text: detail.sex }],
                        style: "header_info",
                      },
                    ],
                    [
                      {
                        text: [
                          { text: isEng ? "Birthdate: " : "วันเดือนปีเกิด: " },
                          {
                            text: isEng
                              ? moment(detail.birthdate).format("D MMM YYYY")
                              : formatADDateToThaiTextDate(detail.birthdate, true),
                          },
                        ],
                        style: "header_info",
                      },
                      {
                        text: [{ text: isEng ? "Age: " : "อายุ: " }, { text: detail.age }],
                        style: "header_info",
                      },
                    ],
                    [
                      {
                        text: [
                          { text: isEng ? "VN: " : "หมายเลขรับบริการ: " },
                          { text: detail.visit_number },
                        ],
                        style: "header_info",
                      },
                      {
                        text: [
                          { text: isEng ? "Visit: " : "วันที่รับบริการ: " },
                          {
                            text: isEng
                              ? moment(detail.visit_date).format("DD/MM/YYYY")
                              : formatISOtoBEDateTime(detail.visit_date).date,
                          },
                        ],
                        style: "header_info",
                      },
                    ],
                  ],
                },
                layout: {
                  paddingLeft: () => 2,
                  paddingRight: () => 2,
                  paddingTop: () => 1,
                  paddingBottom: () => 1,
                  vLineWidth: () => 0,
                  hLineWidth: () => 0,
                },
              },
            ],
            [
              {
                text: [
                  { text: isEng ? "Package: " : "แพ็กเกจ: " },
                  { text: detail.package_name || "" },
                ],
                colSpan: 2,
              },
              {},
            ],
          ],
        },
        layout: {
          vLineWidth: () => 0,
          hLineWidth: (i, node) => (i === node.table.body.length ? 1 : 0),
          hLineStyle: (i, node) => {
            if (i === node.table.body.length) {
              return { dash: { length: 4, space: 2 } };
            }
            return null;
          },
        },
        margin: props.headerMargins ?? [40, 10, 40, 0],
      };
    },
  };
};

/* ------------------------------------------------------ */

/*                          Utils                         */

/* ------------------------------------------------------ */

//* Truncate Text Simple
export const truncateTextSimple = (text: string, maxWidth: number, fontSize: number) => {
  const charWidth = fontSize * 0.6; // ประมาณ

  const maxChars = Math.floor(maxWidth / charWidth);

  if (text.length > maxChars) {
    return `${text.slice(0, Math.max(0, maxChars - 3))}...`;
  }

  return text;
};

type FontWidthCalculator = {
  getTextWidth: (text: string, fontSize: number) => number;
};

type TruncateTextOptions = {
  maxWidth: number;
  fontSize: number;
  font?: string;
  ellipsis?: string;
};

const fontWidthCalculators: Record<string, FontWidthCalculator> = {
  THSarabunNew: {
    getTextWidth: (text: string, fontSize: number) => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Cannot create canvas context!");

      context.font = `${fontSize} px THSarabunNew`;
      return context.measureText(text).width;
    },
  },
};

//* Truncate Text
export const truncateText = (text: string, options: TruncateTextOptions): string => {
  const { maxWidth, fontSize, font = "THSarabunNew", ellipsis = "..." } = options;

  const fontCalculator = fontWidthCalculators[font];
  if (!fontCalculator) {
    throw new Error(`Unsupported font: ${font}`);
  }

  const initialWidth = fontCalculator.getTextWidth(text, fontSize);
  if (initialWidth <= maxWidth) {
    return text;
  }

  if (fontCalculator.getTextWidth(ellipsis, fontSize) > maxWidth) {
    return ellipsis;
  }

  let left = 0;
  let right = text.length;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const truncatedText = text.slice(0, mid) + ellipsis;
    const truncatedWidth = fontCalculator.getTextWidth(truncatedText, fontSize);

    if (truncatedWidth === maxWidth) {
      return truncatedText;
    }

    if (truncatedWidth < maxWidth) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  let result = text.slice(0, right) + ellipsis;
  while (fontCalculator.getTextWidth(result, fontSize) > maxWidth && right > 0) {
    right--;
    result = text.slice(0, right) + ellipsis;
  }

  return result;
};

type TextWidthProps = {
  maxWidthText: number;
  minWidthText: number;
  midWidthText: number;
};

// ใช้หาความกว้างของ text
export const getTextWidth = async ({
  text,
  fontName,
  fontSize,
}: {
  text: string;
  fontName?: string;
  fontSize?: number;
}) => {
  const textContent = {
    text: text,
    font: fontName,
    fontSize: fontSize,
  };
  const temp: any[] = [];
  temp.push(textContent);
  const pdfMake = await getPdfMake(true);
  const pdfTemp = pdfMake.createPdf({
    content: temp,
    defaultStyle: { font: "THSarabunNew", fontSize: 12 },
  }); // default Style

  return new Promise<TextWidthProps>((resolve, reject) => {
    pdfTemp.getBuffer(() => {
      try {
        // console.log("Pdf Info: ", pdf);
        const tempDoc = (pdfTemp as any).docDefinition;
        const defaultFont = tempDoc.defaultStyle.font;
        const defaultFontSize = tempDoc.defaultStyle.fontSize;

        const resolvedFontName = fontName || defaultFont;
        const resolvedFontSize = fontSize || defaultFontSize;

        const tempContent: any[] = [
          {
            text: text,
            font: resolvedFontName,
            fontSize: resolvedFontSize,
          },
        ];

        const pdf = pdfMake.createPdf({ content: tempContent });
        pdf.getBuffer(() => {
          const docContent = (pdf as any).docDefinition.content[0];
          const maxWidth = docContent._maxWidth;
          const minWidth = docContent._minWidth;
          const midWidth = (maxWidth + minWidth) / 2;
          // console.log("Doc Def info: ", docContent);
          resolve({
            maxWidthText: maxWidth,
            minWidthText: minWidth,
            midWidthText: midWidth,
          });
        });
      } catch (error) {
        reject(error);
      }
    });
  });
};

//* ยังไม่แน่ใจว่าจะใช้สำหรับ use case อะไร (เอา width ของแต่ละ col ใน table)
const getTableWidths = async (docDef: any) => {
  // * Note A4 Size width in pts = 595 (estimated)
  // * In PdfMake width in pts = 567.28 (595 - 567.28 = 27.72)
  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  return new Promise((resolve, reject) => {
    pdf.getBuffer((buffer: any) => {
      try {
        const tempDoc = (pdf as any).docDefinition;
        const docContent = tempDoc.content[0];
        // console.log("Yeti Pdf Data: ", docContent);
        const tableWidths: any[] = docContent.table.widths;
        resolve({ tableData: docContent, tableWidths: tableWidths });
      } catch (error) {
        reject(error);
      }
    });
  });
};

// For FormListExpenseInvoiceIpd
//* tableIndex -> Index ของ table ที่ต้องการ (เพราะโยน docDef มาทั้งหมด) (default = 0 คือ Table ตัวแรกของ Content)
export const getTableBreakIndex = async (docDef: any, tableIndex: number = 0) => {
  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  return new Promise((resolve, reject) => {
    pdf.getBuffer((buffer: any) => {
      try {
        const tempDoc = (pdf as any).docDefinition;
        const docContent = tempDoc.content[tableIndex];
        resolve({
          tableData: docContent,
          pageBreakIndex: docContent?._breaksBySpan ? docContent?._breaksBySpan[0].rowIndex : 0,
        });
      } catch (error) {
        reject(error);
      }
    });
  });
};

/**============================================
 **           pdfMake Utils Testing
 *=============================================**/
//* Test Text Width
// (async () => {
//   const myText = "This is Simple Text for Testing";
//   const fontSize = 12;
//   const fontName = "THSarabunNew";

//   try {
//     const textWidth = await getTextWidth({
//       text: myText,
//       fontName: fontName,
//       fontSize: fontSize
//     });
//     console.log("pdfMake Utils Testing: [getTextWidth] Result: ", textWidth);
//   } catch (error) {
//     console.log("pdfMake Utils Testing: [getTextWidth] Error: ", error);
//   }
// })();

//* Test Table Widths
// (async () => {
//   const testDocDef = {
//     pageMargins: [0, 0, 0, 0],
//     pageSize: "A4",
//     defaultStyle: {
//       font: "THSarabunNew",
//       fontSize: 12,
//       lineHeight: 1,
//     },
//     content: [
//       {
//         table: {
//           widths: ["15%", "*", "auto", 100], //* Check true widths
//           body: [
//             [
//               { text: "Col 1" },
//               { text: "Second Column" },
//               { text: "Unknown Width Col" },
//               { text: "Column 3" }
//             ]
//           ]
//         }
//       }
//     ]
//   };

//   try {
//     const result = await getTableWidths(testDocDef);
//     console.log("pdfMake Utils Testing: [getTableWidths] Result: ", result);
//   } catch (error) {
//     console.error("pdfMake Utils Testing: [getTableWidths] Error: ", error);
//   }
// })();
